import { BellIcon } from '@phosphor-icons/react';

import { formatEasternTime } from '@/lib/date';

import type { Notification } from '../schemas';

import styles from './notification-row.module.scss';

export type NotificationRowProps = {
  notification: Notification;
  onSelect: (notification: Notification) => void;
};

/**
 * One notification (Figma 621:549480).
 *
 * Unread and read differ in three ways at once, which is what makes the state legible at a glance
 * without a label: the row takes a tinted background, the bell badge fills brand-700 with a white
 * glyph, and an amber dot sits on the badge. A read row drops all three.
 *
 * It is a `<button>` because the whole row navigates (§ NAVIGATION). The time is `<time>` with a
 * machine-readable `dateTime`, so the ET wall-clock string on screen is not the only record of
 * when this happened.
 */
export function NotificationRow({ notification, onSelect }: NotificationRowProps) {
  const unread = !notification.read;
  const time = formatEasternTime(notification.createdAt);

  return (
    <button
      type="button"
      className={[styles.row, unread ? styles.unread : undefined].filter(Boolean).join(' ')}
      onClick={() => onSelect(notification)}
    >
      <span className={styles.badge} aria-hidden>
        <BellIcon size={18} weight={unread ? 'fill' : 'regular'} />
        {unread ? <span className={styles.dot} /> : null}
      </span>

      <span className={styles.content}>
        <span className={styles.title}>{notification.title}</span>
        {notification.description ? (
          <span className={styles.description}>{notification.description}</span>
        ) : null}
      </span>

      {time ? (
        <time className={styles.time} dateTime={notification.createdAt}>
          {time}
        </time>
      ) : null}

      {/* The visual unread treatment is colour-only, so it needs a text equivalent. */}
      {unread ? <span className={styles.srOnly}>Unread</span> : null}
    </button>
  );
}
